import { useState, useEffect, useRef, useCallback } from 'react'
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import Panel from './Panel'
import { resolveApiBase } from '../../lib/mountBase'

interface XpraPanelProps {
  title: string
  icon: React.ReactNode
  /** Base URL for the Xpra HTML5 client (e.g., /browser/ or /claude/) */
  baseUrl: string
  /** URL to open in browser (only for browser panel) */
  startUrl?: string
  isMaximized?: boolean
  onMaximize?: () => void
  onClose?: () => void
  closeable?: boolean
}

// Progressive connection states for better UX
type ConnectionStatus =
  | 'checking'      // Querying backend status
  | 'starting'      // Container starting
  | 'initializing'  // Waiting for browser service
  | 'connecting'    // Iframe loading
  | 'connected'
  | 'disconnected'
  | 'error'

const statusMessages: Record<ConnectionStatus, string> = {
  checking: 'Checking workspace status...',
  starting: 'Starting workspace...',
  initializing: 'Initializing browser service...',
  connecting: 'Connecting...',
  connected: 'Connected',
  disconnected: 'Disconnected',
  error: 'Connection failed',
}

/**
 * Extract app name from baseUrl like /workspace/app/{appName}/browser/
 */
function extractAppNameFromUrl(baseUrl: string): string | null {
  const match = baseUrl.match(/\/workspace\/app\/([^/]+)\//)
  return match ? decodeURIComponent(match[1]) : null
}

export default function XpraPanel({
  title,
  icon,
  baseUrl,
  startUrl,
  isMaximized,
  onMaximize,
  onClose,
  closeable = true,
}: XpraPanelProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [status, setStatus] = useState<ConnectionStatus>('checking')
  const [error, setError] = useState<string | null>(null)
  const [iframeUrl, setIframeUrl] = useState<string | null>(null)
  const retryCountRef = useRef(0)
  const maxRetries = 3
  // Track if we've already sent start_url in this component instance
  const startUrlSentRef = useRef(false)

  // Extract app name from baseUrl for status polling
  const appName = extractAppNameFromUrl(baseUrl)
  const apiBase = resolveApiBase(typeof window !== 'undefined' ? window.location.pathname : '/')

  // Check if we recently opened this URL (within last 500ms)
  // This only prevents duplicates from React strict mode double-mounting
  const wasRecentlyOpened = useCallback((url: string): boolean => {
    const key = `xpra-opened-time-${baseUrl}-${url}`
    const timestamp = sessionStorage.getItem(key)
    if (!timestamp) return false
    const elapsed = Date.now() - parseInt(timestamp, 10)
    // Consider "recent" only if opened within last 500ms (strict mode protection)
    return elapsed < 500
  }, [baseUrl])

  const markAsOpened = useCallback((url: string) => {
    const key = `xpra-opened-time-${baseUrl}-${url}`
    sessionStorage.setItem(key, Date.now().toString())
  }, [baseUrl])

  // Build the Xpra client URL with auto-connect parameters
  const getXpraUrl = useCallback(() => {
    // Detect mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

    // Xpra HTML5 client parameters for auto-connect and fullscreen mode
    const params = new URLSearchParams({
      // Auto-connect when client loads
      'auto': 'true',
      // Keyboard settings: enable only on mobile (for native keyboard support)
      'keyboard': isMobile ? 'true' : 'false',
      'keyboard_button': 'false',  // Never show the keyboard button
      // CRITICAL: Prevent focus lock issues - don't grab pointer/keyboard
      'pointer_grab': 'false',  // Don't lock the pointer
      'keyboard_grab': 'false',  // Don't grab keyboard focus
      'steal_focus': 'false',    // Don't steal focus from other panels
      // Disable floating menu toolbar
      'floating_menu': 'false',
      // Clipboard integration
      'clipboard': 'true',
      'clipboard_direction': 'both',
      // Video encoding preferences (favor quality)
      'encoding': 'auto',
      // Disable notifications (we handle them in React)
      'notifications': 'false',
      // Sound (disabled for now)
      'sound': 'false',
    })
    // Pass start URL if:
    // 1. We have a startUrl
    // 2. We haven't already sent it in this component instance (prevents duplicates on reconnect)
    // 3. It wasn't opened very recently (prevents duplicates on quick remounts like panel resize)
    if (startUrl && !startUrlSentRef.current && !wasRecentlyOpened(startUrl)) {
      params.set('start_url', startUrl)
      startUrlSentRef.current = true
      markAsOpened(startUrl)
    }
    return `${baseUrl}?${params.toString()}`
  }, [baseUrl, startUrl, wasRecentlyOpened, markAsOpened])

  // Handle iframe load
  const handleLoad = useCallback(() => {
    setStatus('connected')
    setError(null)
    retryCountRef.current = 0
  }, [])

  // Handle iframe error
  const handleError = useCallback(() => {
    if (retryCountRef.current < maxRetries) {
      retryCountRef.current++
      setStatus('connecting')
      // Retry with a delay
      setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.src = getXpraUrl()
        }
      }, 2000)
    } else {
      setStatus('error')
      setError('Failed to connect to the application')
    }
  }, [getXpraUrl])

  // Reconnect function
  const reconnect = useCallback(() => {
    retryCountRef.current = 0
    setStatus('checking')
    setError(null)
    setIframeUrl(null)
    // Force remount to re-run the initialization
    setTimeout(() => {
      setIframeUrl(getXpraUrl())
      setStatus('connecting')
    }, 100)
  }, [getXpraUrl])

  // Poll backend status until browser service is ready, then load iframe
  useEffect(() => {
    let cancelled = false

    const waitForServicesReady = async (): Promise<boolean> => {
      if (!appName) {
        // Legacy mode without app name - skip polling
        return true
      }

      const maxAttempts = 60 // 30 seconds max
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (cancelled) return false

        try {
          const response = await fetch(`${apiBase}/workspace/app/${encodeURIComponent(appName)}/status`)
          if (!response.ok) {
            await new Promise(r => setTimeout(r, 500))
            continue
          }

          const data = await response.json()

          if (data.container === 'not_found') {
            setStatus('starting')
          } else if (data.container === 'running' && !data.browser_ready) {
            setStatus('initializing')
          } else if (data.container === 'running' && data.browser_ready) {
            return true
          } else {
            setStatus('starting')
          }
        } catch {
          // Network error, keep trying
        }
        await new Promise(r => setTimeout(r, 500))
      }
      return false
    }

    const initializeIframe = async () => {
      const ready = await waitForServicesReady()
      if (cancelled) return

      if (!ready) {
        setStatus('error')
        setError('Browser service failed to start')
        return
      }

      setStatus('connecting')
      setIframeUrl(getXpraUrl())
    }

    initializeIframe()

    return () => {
      cancelled = true
    }
  }, [apiBase, appName, getXpraUrl])

  // Monitor connection via periodic checks
  useEffect(() => {
    if (status !== 'connected') return

    // Check if iframe is still accessible
    const checkConnection = () => {
      try {
        if (iframeRef.current) {
          // Try to access iframe contentWindow - will throw if disconnected
          const win = iframeRef.current.contentWindow
          if (!win) {
            setStatus('disconnected')
          }
        }
      } catch {
        // Cross-origin error is expected, connection is fine
      }
    }

    const interval = setInterval(checkConnection, 5000)
    return () => clearInterval(interval)
  }, [status])

  // Send resize event to Xpra when panel size changes
  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) return

    const resizeObserver = new ResizeObserver(() => {
      // Xpra handles resize automatically when iframe dimensions change
      // We could also post a message to the iframe for more precise control
      try {
        iframe.contentWindow?.postMessage({ type: 'resize' }, '*')
      } catch {
        // Ignore cross-origin errors
      }
    })

    resizeObserver.observe(iframe)
    return () => resizeObserver.disconnect()
  }, [])

  // Focus iframe when clicking inside it (detected via window blur)
  // This allows single-click to both interact AND get keyboard focus
  useEffect(() => {
    const handleWindowBlur = () => {
      // When window loses focus to iframe, ensure iframe has keyboard focus
      setTimeout(() => {
        if (document.activeElement === iframeRef.current) {
          iframeRef.current?.focus()
        }
      }, 0)
    }

    window.addEventListener('blur', handleWindowBlur)
    return () => window.removeEventListener('blur', handleWindowBlur)
  }, [])

  // Render content based on status
  const renderContent = () => {
    if (status === 'error') {
      return (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-background z-10">
          <AlertCircle className="w-8 h-8 text-destructive mb-2" />
          <span className="text-muted-foreground mb-4">{error || 'Connection failed'}</span>
          <button
            onClick={reconnect}
            className="px-3 py-1.5 rounded bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Reconnect
          </button>
        </div>
      )
    }

    const showLoadingOverlay = status === 'checking' || status === 'starting' || status === 'initializing' || status === 'connecting'

    return (
      <>
        {showLoadingOverlay && (
          <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">{statusMessages[status]}</span>
          </div>
        )}
        {iframeUrl && (
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            className="w-full h-full border-0"
            tabIndex={0}
            onLoad={handleLoad}
            onError={handleError}
            allow="clipboard-read; clipboard-write"
            title={title}
          />
        )}
      </>
    )
  }

  return (
    <Panel
      title={title}
      icon={icon}
      isMaximized={isMaximized}
      onMaximize={onMaximize}
      onClose={onClose}
      closeable={closeable}
      extra={
        status === 'connected' && (
          <button
            onClick={reconnect}
            className="p-1 rounded hover:bg-secondary transition-colors"
            title="Refresh connection"
          >
            <RefreshCw className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        )
      }
    >
      <div className="relative h-full bg-background">
        {renderContent()}
      </div>
    </Panel>
  )
}
